import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { Button } from '../Button/Button';
import './OverflowMenu.css';

export interface OverflowMenuItem {
  /** Stable key of the item. */
  id: string;
  label: string;
  onSelect: () => void;
  /** 'danger' for destructive actions (delete); shown in the danger style. */
  tone?: 'default' | 'danger';
}

export interface OverflowMenuProps {
  /** Accessible name of the "···" trigger, e.g. "More actions for Zeostart". */
  label: string;
  /** The actions; the menu renders nothing when the list is empty. */
  items: readonly OverflowMenuItem[];
  className?: string;
}

/**
 * "···" menu for secondary and destructive row actions, so delete never sits
 * between routine buttons (usability audit §5.8). The one implementation of the
 * menu-button pattern: Enter/Space/ArrowDown open it on the first item, arrow
 * keys move, Home/End jump, Escape and Tab close it, Escape and a choice return
 * focus to the trigger, a click outside closes it.
 */
export function OverflowMenu({ label, items, className = '' }: OverflowMenuProps) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    if (!open) return;
    itemRefs.current[0]?.focus();
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  const focusItem = (index: number) => {
    const count = items.length;
    itemRefs.current[(index + count) % count]?.focus();
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const current = itemRefs.current.findIndex((el) => el === document.activeElement);
    switch (event.key) {
      case 'ArrowDown': event.preventDefault(); focusItem(current + 1); break;
      case 'ArrowUp': event.preventDefault(); focusItem(current - 1); break;
      case 'Home': event.preventDefault(); focusItem(0); break;
      case 'End': event.preventDefault(); focusItem(items.length - 1); break;
      case 'Escape': event.preventDefault(); close(true); break;
      case 'Tab': close(false); break;
      default: break;
    }
  };

  // Nothing to offer: no "···" button that opens an empty menu.
  if (items.length === 0) return null;

  return (
    <div className={`rg-overflow-menu ${className}`.trim()} ref={rootRef}>
      <Button
        ref={triggerRef}
        variant="ghost"
        size="sm"
        className="rg-overflow-menu__trigger"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span aria-hidden="true">···</span>
      </Button>
      {open && (
        <div id={menuId} className="rg-overflow-menu__list" role="menu" aria-label={label} tabIndex={-1} onKeyDown={onMenuKeyDown}>
          {items.map((item, index) => (
            <Button
              key={item.id}
              ref={(el) => { itemRefs.current[index] = el; }}
              variant="ghost"
              size="sm"
              role="menuitem"
              tabIndex={-1}
              className={`rg-overflow-menu__item${item.tone === 'danger' ? ' rg-overflow-menu__item--danger' : ''}`}
              onClick={() => {
                close(true);
                item.onSelect();
              }}
            >
              {item.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
